import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import "@fontsource/ibm-plex-mono";
import { CustomSVG } from '../components/CustomSVG';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useDispatch } from '../redux/hooks';
import { openLogin } from '../redux/slices/modalSlice';
import { addToCart } from '../redux/slices/cartSlice';
import { addToFavorites } from '../redux/slices/favoritesSlice';

const Container = styled.div`
  background-color: white;
  width: 100%;
  padding: 40px 10px;
  display: flex;
  justify-content: center;
  font-family: 'IBM Plex Mono', monospace;
`;

const Content = styled.div`
  width: 100%;
  max-width: 984px;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const TopSection = styled.div`
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  justify-content: center;
`;

const BookCover = styled.img`
  max-width: 440px;
  height: auto;
  overflow: hidden;
  border-radius: 10px;
  margin: auto;
`;

const Info = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 1024px) {
    max-width: 520px;
  }
`;

const TitleContainer = styled.div`
  position: relative;
  height: 112px;
  display: flex;
  align-items: flex-start;
`;

const Title = styled.h2`
  font-size: 44px;
  font-weight: 1000;
  color: #3B6255;
  margin: 0;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  width: 100%;
`;

const Tooltip = styled.div<{ show: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  margin-top: 8px;
`;

const Badges = styled.div`
  max-width: 514px;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 6px;

  scrollbar-width: thin;
  scrollbar-color: #CBDED3 transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #CBDED3;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #CBDED5;
  }
`;

const Badge = styled.span`
  background: #CBDED3;
  color: #3e7760;
  padding: 6px 12px;
  font-size: 20px;
  font-weight: 500;
  border-radius: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background: #B8D0C5;
    transform: translateY(-1px);
  }
`;

const SectionLabel = styled.h4`
  font-size: 32px;
  margin: 12px 0 0;
  color: #3B6255;
`;

const SpecsTable = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

const SpecsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 20px;
`;

const SpecsRowLeft = styled.div`
  text-align: left;
`;

const SpecsRowRight = styled.div`
  text-align: right;
`;

const ClickableSpec = styled(SpecsRowRight)<React.HTMLAttributes<HTMLDivElement>>`
  cursor: pointer;
  color: black;
  text-decoration: underline;
  transition: text-decoration 0.3s ease;

  &:hover {
    text-decoration: none;
  }
`;

const Price = styled.div`
  font-size: 50px;
  color: #A13C17;
  font-weight: 900;
`;

const Buttons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
`;

const Button = styled.button<{ disabled?: boolean }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 900;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease, color 0.3s ease;

  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
    opacity: 0.6;
  `}
`;

const CartButton = styled(Button)<React.ButtonHTMLAttributes<HTMLButtonElement>>`
  display: flex;
  justify-content: space-around;
  background: white;
  border: 5px solid #A13C17;
  border-radius: 50px;
  color: #A13C17;
  width: 408px;

  &:hover:not(:disabled) {
    background: #f2ded7;
  }
`;

const WishButton = styled(Button)<React.ButtonHTMLAttributes<HTMLButtonElement>>`
  background: #fff;
  color: #A13C17;
  border: 5px solid #A13C17;
  border-radius: 50px;
  width: 82px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f2ded7;
    color: white;
  }
`;

const Description = styled.p`
  font-size: 24px;
  color: black;
  text-indent: 50px;
  line-height: 1.6;
  text-align: justify;
`;

const BookDetailPage: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const [book, setBook] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const customerId = useSelector((state: RootState) => state.auth.customer_id);
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const dispatch = useDispatch();

  const cartItems = useSelector((state: RootState) => state.cart.items);
  const addedToCart = id ? cartItems.some(item => item.id.toString() === id) : false;
  const wishlist = useSelector((state: RootState) => state.favorites.items);
  let addedToWish: boolean = false;
  if (wishlist.length) {
    addedToWish = wishlist.some((book) => book.book_id === Number(id));
  }


  const handleAddToCart = useCallback(async () => {
    if (!token) {
      dispatch(openLogin());
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/shopping-cart?customerId=${customerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_id: customerId,
          book_id: id,
          book_quantity: 1,
        }),
      });

      if (!res.ok) {
        throw new Error(`Помилка при додаванні: ${res.status}`);
      }

      const data = await res.json();
      dispatch(addToCart({
        id: data.book_id,
        quantity: data.book_quantity,
        item_id: data.item_id,
        customer_id: data.customer_id,
      }));
      console.log('Додано в кошик:', data);
    } catch (error) {
      console.error('Не вдалося додати до кошика', error);
    }
  }, [dispatch, customerId, id, token]);

  const handleAddToWish = useCallback(async () => {
    if (!token) {
      dispatch(openLogin());
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/wishlist?customerId=${customerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_id: customerId,
          book_id: id,
        }),
      });

      if (!res.ok) {
        throw new Error(`Помилка при додаванні: ${res.status}`);
      }

      const data = await res.json();
      dispatch(addToFavorites(data));
      console.log('Додано в вішліст:', data);
    } catch (error) {
      console.error('Не вдалося додати до вішліста', error);
    }
  }, [dispatch, customerId, id, token]);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const bookRes = await fetch(`${process.env.REACT_APP_BASE_API_URL}/books/${id}`);
        const bookData = await bookRes.json();

        const [
        author,
        publisher,
        coverType,
        format,
        language,
        genres
        ] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_API_URL}/authors/${bookData.author_id}`).then(r => r.json()),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/publishers/${bookData.publisher_id}`).then(r => r.json()),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/cover-types/${bookData.cover_type_id}`).then(r => r.json()),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/formats/${bookData.format_id}`).then(r => r.json()),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/languages/${bookData.language_id}`).then(r => r.json()),
          Promise.all(
            bookData.genre_ids.map((id: number) =>
              fetch(`${process.env.REACT_APP_BASE_API_URL}/genres/${id}`).then(r => r.json())
            )
          )
        ]);

        setBook({
          ...bookData,
          author,
          publisher,
          cover_type: coverType,
          format,
          language,
          genres,
        });

      } catch (error) {
        console.error('Помилка при завантаженні книги:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [id, handleAddToCart, handleAddToWish, customerId, token, addedToWish, addedToCart]);

  const needsTooltip = (text: string) => {
    return text.length > 40;
  };

  const handleGenreClick = (genreId: number) => {
    navigate(`/catalog?genre=${genreId}`);
  };

  const handleAuthorClick = () => {
    navigate(`/catalog?author=${book.author_id}`);
  };

  const handlePublisherClick = () => {
    navigate(`/catalog?publisher=${book.publisher_id}`);
  };

  const handleLanguageClick = () => {
    navigate(`/catalog?language=${book.language_id}`);
  };

  if (loading) {
    return (
      <Container>
        <Content>Завантаження...</Content>
      </Container>
    );
  }

  if (!book) {
    return (
      <Container>
        <Content>Книга не знайдена</Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <TopSection>
          <BookCover
            src={book.book_cover_path}
            alt={book.book_title}
          />
          <Info>
            <TitleContainer>
              <Title
                onMouseEnter={() => needsTooltip(book.book_title) && setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                {book.book_title}
              </Title>
              {needsTooltip(book.book_title) && (
                <Tooltip show={showTooltip}>
                  {book.book_title}
                </Tooltip>
              )}
            </TitleContainer>
            
            <Badges>
              {book.genres?.map((g: any) => (
                <Badge 
                  key={g.genre_id} 
                  onClick={() => handleGenreClick(g.genre_id)}
                >
                  {g.genre_name}
                </Badge>
              ))}
            </Badges>

            <SectionLabel>Характеристики:</SectionLabel>
            <SpecsTable>
              <SpecsColumn>
                <SpecsRowLeft>Автор:</SpecsRowLeft>
                <SpecsRowLeft>Видавництво:</SpecsRowLeft>
                <SpecsRowLeft>Палітурка:</SpecsRowLeft>
                <SpecsRowLeft>Кількість сторінок:</SpecsRowLeft>
                <SpecsRowLeft>Формат:</SpecsRowLeft>
                <SpecsRowLeft>Мова:</SpecsRowLeft>
                <SpecsRowLeft>Рік видання:</SpecsRowLeft>
              </SpecsColumn>
              <SpecsColumn>
                <ClickableSpec onClick={handleAuthorClick}>
                  {book.author?.author_name}
                </ClickableSpec>
                <ClickableSpec onClick={handlePublisherClick}>
                  {book.publisher?.name}
                </ClickableSpec>
                <SpecsRowRight>{book.cover_type?.type}</SpecsRowRight>
                <SpecsRowRight>{book.book_page_count}</SpecsRowRight>
                <SpecsRowRight>{book.format?.format}</SpecsRowRight>
                <ClickableSpec onClick={handleLanguageClick}>
                  {book.language?.name}
                </ClickableSpec>
                <SpecsRowRight>{book.book_publication_year}</SpecsRowRight>
              </SpecsColumn>
            </SpecsTable>

            <Price>{book.book_price} грн</Price>

            <Buttons>
              <CartButton onClick={handleAddToCart} disabled={addedToCart || book.book_quantity <= 0}>
                <CustomSVG
                  src={`/media/Cart.svg`}
                  width="45px"
                  height="44px"
                  color='#A13C17'
                />
                {book.book_quantity > 0 ? (addedToCart ? 'Додано в кошик' : 'Додати в кошик') : 'Немає в наявності'}
              </CartButton>
              
              <WishButton onClick={handleAddToWish} disabled={addedToWish}>
                <CustomSVG
                  src={`/media/Bookmark.svg`}
                  width="29px"
                  height="41px"
                  color='#A13C17'
                />
              </WishButton>
            </Buttons>
          </Info>
        </TopSection>

        <Description>{book.book_description}</Description>
      </Content>
    </Container>
  );
};

export default BookDetailPage;